/**
 * AdminChurchHomeGroupSplit — drag-and-drop bulk reassignment.
 *
 * Route: /admin/churches/:churchId/home-group-split
 *
 * Layout: horizontal scroll of "columns" (Unassigned + one per home group).
 * Each column contains member "chips" the admin can drag between columns.
 * Local pending moves are batched and committed via the bulk-assign
 * endpoint so a large reshuffle is one API call.
 *
 * Uses native HTML5 drag & drop — no extra dependency.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Home, Users, UserPlus, RotateCcw, Save, Search,
  GripHorizontal, CheckSquare, Square, MoveRight,
  MessageSquare, FileText, Send,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const UNASSIGNED_COL = '__unassigned__';

const initials = (m) => {
  const f = (m.first_name || '').charAt(0);
  const l = (m.last_name || '').charAt(0);
  return `${f}${l}`.toUpperCase() || '?';
};

export default function AdminChurchHomeGroupSplit() {
  const { churchId } = useParams();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState({});         // memberId → colId
  const [originals, setOriginals] = useState({});             // baseline
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());        // memberIds selected for bulk-move
  const [messageColumn, setMessageColumn] = useState(null);   // { id, name } for bulk-message dialog

  const q = useQuery({
    queryKey: ['hg-split', churchId],
    queryFn: async () => {
      const r = await axios.get(
        `${API}/api/admin/churches/${churchId}/home-group-split`,
        { headers: authH() },
      );
      return r.data;
    },
    enabled: !!churchId,
  });

  // Hydrate local state whenever fresh data arrives
  useEffect(() => {
    if (!q.data) return;
    const map = {};
    (q.data.members || []).forEach((m) => {
      map[m.id] = m.home_group_id || UNASSIGNED_COL;
    });
    setAssignments(map);
    setOriginals(map);
  }, [q.data]);

  const bulkAssign = useMutation({
    mutationFn: async () => {
      const moves = pendingMoves.map((memberId) => ({
        member_id: memberId,
        home_group_id: assignments[memberId] === UNASSIGNED_COL ? null : assignments[memberId],
      }));
      const r = await axios.post(
        `${API}/api/admin/churches/${churchId}/home-group-split/bulk-assign`,
        { moves },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: (data) => {
      toast.success(`Moved ${data.moved} · unchanged ${data.unchanged}${data.errors?.length ? ` · errors ${data.errors.length}` : ''}`);
      q.refetch();
    },
    onError: (e) => toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`),
  });

  const [statementsLoadingColumn, setStatementsLoadingColumn] = useState(null);

  const downloadStatements = async (col) => {
    setStatementsLoadingColumn(col.id);
    try {
      const r = await axios.post(
        `${API}/api/admin/churches/${churchId}/home-group-split/columns/${col.id}/bulk-statements?period_days=365`,
        {},
        { headers: authH(), responseType: 'blob' },
      );
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statements-${(col.name || 'home-group').toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const generated = r.headers['x-statements-generated'];
      const skipped = r.headers['x-statements-skipped'];
      toast.success(`Downloaded ${generated} statement${generated === '1' ? '' : 's'}${skipped && skipped !== '0' ? ` · skipped ${skipped}` : ''}`);
    } catch (e) {
      toast.error(`Statements failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setStatementsLoadingColumn(null);
    }
  };

  const membersById = useMemo(() => {
    const map = {};
    (q.data?.members || []).forEach((m) => { map[m.id] = m; });
    return map;
  }, [q.data]);

  const pendingMoves = useMemo(
    () => Object.keys(assignments).filter((mid) => assignments[mid] !== originals[mid]),
    [assignments, originals],
  );

  const columns = useMemo(() => {
    const hg = q.data?.home_groups || [];
    return [
      { id: UNASSIGNED_COL, name: 'Unassigned', tone: 'slate', city: null },
      ...hg.map((g) => ({ id: g.id, name: g.name, city: g.city, tone: 'amber' })),
    ];
  }, [q.data]);

  const byColumn = useMemo(() => {
    const map = {};
    columns.forEach((c) => { map[c.id] = []; });
    Object.keys(assignments).forEach((mid) => {
      const col = assignments[mid] || UNASSIGNED_COL;
      if (map[col]) map[col].push(mid);
    });
    // sort each column alphabetically by last name
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const ma = membersById[a] || {};
        const mb = membersById[b] || {};
        return (ma.last_name || '').localeCompare(mb.last_name || '');
      });
    });
    return map;
  }, [assignments, columns, membersById]);

  // Drag & drop handlers
  const onDragStart = (memberId) => (e) => {
    // If the user has multi-selected items and drags one of them, treat
    // the drag as a bulk move of all selected chips.
    setDragging(memberId);
    try { e.dataTransfer.setData('text/plain', memberId); } catch { /* ignore */ }
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragEnd = () => { setDragging(null); setDropTarget(null); };
  const onDragOver = (colId) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(colId);
  };
  const onDrop = (colId) => (e) => {
    e.preventDefault();
    const memberId = dragging || e.dataTransfer.getData('text/plain');
    // If the dragged member is part of the selection, move the WHOLE
    // selection into this column in one shot. Otherwise just move the
    // single chip (backwards compatible).
    if (memberId && selected.has(memberId) && selected.size > 0) {
      setAssignments((a) => {
        const next = { ...a };
        selected.forEach((mid) => {
          if (next[mid] !== colId) next[mid] = colId;
        });
        return next;
      });
    } else if (memberId && assignments[memberId] !== colId) {
      setAssignments((a) => ({ ...a, [memberId]: colId }));
    }
    setDragging(null);
    setDropTarget(null);
  };

  const toggleSelect = (memberId) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());
  const selectVisibleInColumn = (colId) => {
    const chipIds = byColumn[colId] || [];
    const searchLower = search.trim().toLowerCase();
    const visibleIds = searchLower
      ? chipIds.filter((mid) => {
          const m = membersById[mid] || {};
          const hay = `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''} ${m.phone || ''}`.toLowerCase();
          return hay.includes(searchLower);
        })
      : chipIds;
    setSelected((s) => {
      const next = new Set(s);
      visibleIds.forEach((mid) => next.add(mid));
      return next;
    });
  };

  const bulkMoveTo = (colId) => {
    if (selected.size === 0) return;
    setAssignments((a) => {
      const next = { ...a };
      selected.forEach((mid) => { next[mid] = colId; });
      return next;
    });
  };

  const revert = () => { setAssignments(originals); clearSelection(); };

  const searchLower = search.trim().toLowerCase();

  if (q.isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="hg-split-loading">
        Loading split view…
      </div>
    );
  }
  if (q.isError) {
    return (
      <div className="min-h-screen bg-slate-950 p-6 text-rose-300">
        {q.error?.response?.data?.detail || q.error?.message}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="admin-hg-split-page">
      <div className="p-4 sm:p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              className="mb-1 text-slate-400 hover:text-slate-100"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back
            </Button>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Home className="h-6 w-6 text-amber-400" />
              Home Group Split View
            </h1>
            <p className="text-xs text-slate-500">
              {q.data?.totals?.members ?? 0} members across {q.data?.totals?.home_groups ?? 0} home groups
              {' · '}Drag chips between columns to reassign
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
              <Input
                data-testid="hg-split-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter members…"
                className="h-8 w-56 border-slate-700 bg-slate-900 pl-7 text-sm"
              />
            </div>
            <Button
              data-testid="select-mode-toggle"
              variant="outline"
              size="sm"
              onClick={() => { setSelectMode((v) => !v); if (selectMode) clearSelection(); }}
              className={
                selectMode
                  ? 'border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20'
                  : 'border-slate-700 bg-slate-900 hover:bg-slate-800'
              }
            >
              {selectMode
                ? <CheckSquare className="mr-1 h-4 w-4" />
                : <Square className="mr-1 h-4 w-4" />}
              {selectMode ? `${selected.size} selected` : 'Select rows'}
            </Button>
            {selectMode && selected.size > 0 && (
              <Button
                data-testid="clear-selection-btn"
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="text-slate-400 hover:text-slate-100"
              >
                clear
              </Button>
            )}
            <Badge
              variant="outline"
              data-testid="pending-count"
              className={pendingMoves.length ? 'border-amber-500/40 text-amber-300' : 'border-slate-700 text-slate-500'}
            >
              {pendingMoves.length} pending
            </Badge>
            <Button
              data-testid="revert-btn"
              variant="outline"
              size="sm"
              onClick={revert}
              disabled={pendingMoves.length === 0 && selected.size === 0}
              className="border-slate-700 bg-slate-900 hover:bg-slate-800"
            >
              <RotateCcw className="mr-1 h-4 w-4" /> Revert
            </Button>
            <Button
              data-testid="save-btn"
              size="sm"
              className="bg-amber-600 hover:bg-amber-500"
              onClick={() => bulkAssign.mutate()}
              disabled={pendingMoves.length === 0 || bulkAssign.isPending}
            >
              <Save className="mr-1 h-4 w-4" />
              {bulkAssign.isPending ? 'Saving…' : `Save ${pendingMoves.length}`}
            </Button>
          </div>
        </div>

        {/* Columns */}
        <div className="overflow-x-auto pb-4" data-testid="hg-split-board">
          <div className="flex gap-3 min-w-min">
            {columns.map((col) => {
              const chipIds = byColumn[col.id] || [];
              const visibleIds = searchLower
                ? chipIds.filter((mid) => {
                    const m = membersById[mid] || {};
                    const hay = `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''} ${m.phone || ''}`.toLowerCase();
                    return hay.includes(searchLower);
                  })
                : chipIds;
              const isTarget = dropTarget === col.id && dragging;
              const isUnassigned = col.id === UNASSIGNED_COL;
              return (
                <div
                  key={col.id}
                  data-testid={`hg-column-${col.id}`}
                  onDragOver={onDragOver(col.id)}
                  onDrop={onDrop(col.id)}
                  onDragLeave={() => setDropTarget((t) => (t === col.id ? null : t))}
                  className={`flex w-64 flex-shrink-0 flex-col rounded-lg border transition-colors ${
                    isTarget
                      ? 'border-amber-500 bg-amber-500/10'
                      : isUnassigned
                        ? 'border-slate-800 bg-slate-900/50'
                        : 'border-amber-500/20 bg-slate-900/60'
                  }`}
                >
                  <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2">
                    <div className="flex items-center gap-2">
                      {isUnassigned
                        ? <UserPlus className="h-4 w-4 text-slate-400" />
                        : <Home className="h-4 w-4 text-amber-400" />}
                      <div>
                        <div className="text-sm font-semibold">{col.name}</div>
                        {col.city && <div className="text-[10px] text-slate-500">{col.city}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {chipIds.length > 0 && (
                        <>
                          <button
                            type="button"
                            data-testid={`column-message-all-${col.id}`}
                            title={`Send template to all ${chipIds.length} member(s)`}
                            onClick={() => setMessageColumn({ id: col.id, name: col.name, count: chipIds.length })}
                            className="rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-300 hover:bg-sky-500/20"
                          >
                            <MessageSquare className="inline h-3 w-3" /> msg
                          </button>
                          <button
                            type="button"
                            data-testid={`column-statements-${col.id}`}
                            disabled={statementsLoadingColumn === col.id}
                            title={`Print combined statements for all ${chipIds.length} member(s)`}
                            onClick={() => downloadStatements(col)}
                            className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
                          >
                            <FileText className="inline h-3 w-3" />{' '}
                            {statementsLoadingColumn === col.id ? '…' : 'PDF'}
                          </button>
                        </>
                      )}
                      {selectMode && (
                        <>
                          <button
                            type="button"
                            data-testid={`select-column-${col.id}`}
                            title="Select every visible member in this column"
                            onClick={() => selectVisibleInColumn(col.id)}
                            className="rounded border border-slate-800 bg-slate-950 px-1.5 py-0.5 text-[10px] text-slate-400 hover:border-violet-500/40 hover:text-violet-300"
                          >
                            + all
                          </button>
                          {selected.size > 0 && (
                            <button
                              type="button"
                              data-testid={`bulk-move-here-${col.id}`}
                              title={`Move ${selected.size} selected here`}
                              onClick={() => bulkMoveTo(col.id)}
                              className="rounded border border-violet-500/40 bg-violet-500/10 px-1.5 py-0.5 text-[10px] text-violet-300 hover:bg-violet-500/20"
                            >
                              <MoveRight className="inline h-3 w-3" /> {selected.size}
                            </button>
                          )}
                        </>
                      )}
                      <Badge variant="outline" className="border-slate-700 text-xs text-slate-400">
                        {chipIds.length}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex-1 space-y-1 overflow-y-auto p-2" style={{ minHeight: 300, maxHeight: 600 }}>
                    {visibleIds.length === 0 && (
                      <div className="p-2 text-center text-xs text-slate-500">
                        {searchLower ? 'No matches' : 'Drop members here'}
                      </div>
                    )}
                    {visibleIds.map((mid) => {
                      const m = membersById[mid] || {};
                      const isDirty = assignments[mid] !== originals[mid];
                      const isSelected = selected.has(mid);
                      return (
                        <div
                          key={mid}
                          draggable
                          onDragStart={onDragStart(mid)}
                          onDragEnd={onDragEnd}
                          data-testid={`member-chip-${mid}`}
                          data-dirty={isDirty ? 'true' : 'false'}
                          data-selected={isSelected ? 'true' : 'false'}
                          className={`group flex cursor-grab items-center gap-2 rounded border px-2 py-1 text-xs transition-colors active:cursor-grabbing ${
                            isSelected
                              ? 'border-violet-500/60 bg-violet-500/10 text-violet-200'
                              : isDirty
                                ? 'border-amber-500/60 bg-amber-500/10 text-amber-200'
                                : 'border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700 hover:bg-slate-900'
                          } ${dragging === mid ? 'opacity-40' : ''}`}
                        >
                          {selectMode && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleSelect(mid); }}
                              data-testid={`select-chip-${mid}`}
                              className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border border-slate-700 bg-slate-950 hover:border-violet-500"
                            >
                              {isSelected && <CheckSquare className="h-3 w-3 text-violet-300" />}
                            </button>
                          )}
                          <GripHorizontal className="h-3 w-3 opacity-50 group-hover:opacity-100" />
                          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-800 text-[10px] font-semibold text-slate-300">
                            {initials(m)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate">
                              {m.first_name} {m.last_name}
                            </div>
                            <div className="truncate text-[10px] text-slate-500">
                              {m.email || m.phone || m.role || '—'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="text-center text-[10px] text-slate-500">
          <Users className="mr-1 inline h-3 w-3" />
          Bulk moves are one API call · every reassignment is audited to church_member_actions.
        </p>

        {messageColumn && (
          <BulkMessageDialog
            churchId={churchId}
            column={messageColumn}
            onClose={() => setMessageColumn(null)}
          />
        )}
      </div>
    </div>
  );
}


// ── Bulk Message Dialog ─────────────────────────────────────────
function BulkMessageDialog({ churchId, column, onClose }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [channel, setChannel] = useState('auto');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const templatesQ = useQuery({
    queryKey: ['msg-templates'],
    queryFn: async () => (await axios.get(`${API}/api/admin/message-templates`, { headers: authH() })).data,
  });

  const applyTemplate = (id) => {
    setSelectedTemplateId(id);
    if (!id) return;
    const tpl = (templatesQ.data?.items || []).find((t) => t.id === id);
    if (tpl) {
      setSubject(tpl.subject || '');
      setBody(tpl.body || '');
      if (tpl.channel_hint) setChannel(tpl.channel_hint);
    }
  };

  const send = useMutation({
    mutationFn: async () => {
      const r = await axios.post(
        `${API}/api/admin/churches/${churchId}/home-group-split/columns/${column.id}/bulk-message`,
        {
          subject: subject.trim(),
          body: body.trim(),
          channel,
          template_id: selectedTemplateId || undefined,
        },
        { headers: { ...authH(), 'Content-Type': 'application/json' } },
      );
      return r.data;
    },
    onSuccess: (d) => {
      toast.success(`Dispatched to ${d.dispatched_count} · skipped ${d.skipped_count}`);
      onClose();
    },
    onError: (e) => toast.error(`Send failed: ${e?.response?.data?.detail || e.message}`),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="bulk-message-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-sky-300" />
            Message · {column.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-xs text-sky-200">
            One click emits a <code className="text-sky-300">Member_Message_Requested</code> event for
            <span className="mx-1 font-semibold text-white">{column.count}</span>
            member{column.count === 1 ? '' : 's'} in this column. All sends go through the audited notification engine.
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Start from a template (optional)</div>
            <select
              data-testid="bulk-message-template"
              value={selectedTemplateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="">— None —</option>
              {(templatesQ.data?.items || []).map((t) => (
                <option key={t.id} value={t.id}>
                  [{t.category}] {t.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Subject</div>
            <Input
              data-testid="bulk-message-subject"
              placeholder="e.g. Home Group meeting reminder — Wednesday 7pm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-slate-950 border-slate-700"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Body</div>
            <Textarea
              data-testid="bulk-message-body"
              placeholder="Message content — use {{first_name}} to personalise."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="bg-slate-950 border-slate-700 min-h-[120px]"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Channel</div>
            <select
              data-testid="bulk-message-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-200"
            >
              <option value="auto">Auto (prefer email, fall back to SMS)</option>
              <option value="email">Email only</option>
              <option value="sms">SMS only</option>
              <option value="whatsapp">WhatsApp only</option>
            </select>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              data-testid="bulk-message-send"
              className="bg-sky-600 hover:bg-sky-500"
              onClick={() => send.mutate()}
              disabled={!subject.trim() || !body.trim() || send.isPending}
            >
              <Send className="mr-1 h-4 w-4" />
              {send.isPending ? 'Dispatching…' : `Send to ${column.count}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
